/*
 * Complete the Look carousel.
 *
 * Deliberately a custom element rather than a Swiper instance. The block's
 * markup is replaced wholesale (outerHTML) by VariantSelects.renderProductInfo
 * every time the shopper changes variant, which would leave a Swiper instance
 * pointing at detached nodes. A custom element is upgraded automatically each
 * time the fresh markup is inserted, so connectedCallback re-binds against the
 * new DOM with no re-init hook needed anywhere else.
 *
 * Scrolling and snapping are pure CSS (scroll-snap), so touch swipe keeps
 * working even if this script never loads - the buttons are the only
 * JS-dependent part, and they stay hidden until it does.
 *
 * Button enabled/disabled state is driven by a short requestAnimationFrame
 * watch that ends as soon as the scroll position settles, rather than by the
 * scroll event alone. Belt and braces: scroll/scrollend still drive it where
 * they fire, but the watch means the buttons stay correct even where those
 * events are unreliable, and it updates them mid-animation rather than only
 * at the end.
 */
if (!customElements.get("complete-the-look-carousel")) {
  customElements.define(
    "complete-the-look-carousel",
    class CompleteTheLookCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector("[data-ctl-track]");
        if (!this.track) return;

        this.prevButton = this.querySelector("[data-ctl-prev]");
        this.nextButton = this.querySelector("[data-ctl-next]");

        this.onScroll = this.refresh.bind(this);
        this.onSettle = this.watchUntilSettled.bind(this);

        this.track.addEventListener("scroll", this.onScroll, { passive: true });
        this.track.addEventListener("scrollend", this.onScroll);

        // A swipe or trackpad gesture ends without necessarily telling us, so
        // start the settle watch when the gesture does.
        this.track.addEventListener("touchend", this.onSettle, { passive: true });
        this.track.addEventListener("pointerup", this.onSettle);

        if (this.prevButton) {
          this.prevButton.addEventListener("click", () => this.page(-1));
        }
        if (this.nextButton) {
          this.nextButton.addEventListener("click", () => this.page(1));
        }

        if ("ResizeObserver" in window) {
          this.resizeObserver = new ResizeObserver(() => this.refresh());
          this.resizeObserver.observe(this.track);
        }

        this.refresh();
      }

      disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.rafId) cancelAnimationFrame(this.rafId);
        this.watching = false;

        if (!this.track) return;
        this.track.removeEventListener("scroll", this.onScroll);
        this.track.removeEventListener("scrollend", this.onScroll);
        this.track.removeEventListener("touchend", this.onSettle);
        this.track.removeEventListener("pointerup", this.onSettle);
      }

      // One "page" is whatever is currently visible - two cards at the widths
      // set in component-complete-the-look.css.
      page(direction) {
        this.track.scrollBy({
          left: direction * this.track.clientWidth,
          behavior: "smooth",
        });
        this.watchUntilSettled();
      }

      // Refresh every frame until the scroll position stops changing, then stop.
      watchUntilSettled() {
        if (this.watching) return;
        this.watching = true;

        let previous = null;
        let stableFrames = 0;

        const tick = () => {
          this.refresh();

          const current = this.track.scrollLeft;
          stableFrames = current === previous ? stableFrames + 1 : 0;
          previous = current;

          if (stableFrames < 4) {
            this.rafId = requestAnimationFrame(tick);
          } else {
            this.watching = false;
          }
        };

        this.rafId = requestAnimationFrame(tick);
      }

      // Reveal the buttons only when there is actually somewhere to scroll,
      // and disable each one at its end of the track.
      refresh() {
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        const scrollable = maxScroll > 1;

        this.classList.toggle("complete-the-look--scrollable", scrollable);

        if (this.prevButton) {
          this.prevButton.disabled = !scrollable || this.track.scrollLeft <= 1;
        }
        if (this.nextButton) {
          this.nextButton.disabled =
            !scrollable || this.track.scrollLeft >= maxScroll - 1;
        }
      }
    },
  );
}
